import { Loader2, RefreshCw, Send, Trash2 } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useT } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { TelegramConnection, TelegramLoginStatus } from "@/lib/types"

const POLL_INTERVAL_MS = 2000

type Phase = "idle" | "qr" | "password"

/** Connect one Telegram account to the agent by scanning a QR code (with 2FA support). */
export function TelegramPanel({ agentId }: { agentId: string }) {
  const t = useT()
  const [loading, setLoading] = React.useState(true)
  const [configured, setConfigured] = React.useState(true)
  const [connection, setConnection] = React.useState<TelegramConnection | null>(null)
  const [busy, setBusy] = React.useState(false)

  const [phase, setPhase] = React.useState<Phase>("idle")
  const [loginId, setLoginId] = React.useState<string | null>(null)
  const [qrImage, setQrImage] = React.useState<string | null>(null)
  const [password, setPassword] = React.useState("")
  const [passwordError, setPasswordError] = React.useState<string | null>(null)

  const loadState = React.useCallback(async () => {
    try {
      const state = await api.getTelegramState(agentId)
      setConfigured(state.configured)
      setConnection(state.connection)
    } catch {
      toast.error(t("telegramPanel.toasts.loadFailed"))
    } finally {
      setLoading(false)
    }
  }, [agentId, t])

  React.useEffect(() => {
    void loadState()
  }, [loadState])

  const resetLogin = React.useCallback(() => {
    setPhase("idle")
    setLoginId(null)
    setQrImage(null)
    setPassword("")
    setPasswordError(null)
  }, [])

  // Poll the pending login while one is active. The interval owns nothing beyond reading
  // status and moving the phase along; a finished/errored login clears it.
  React.useEffect(() => {
    if (!loginId || phase === "idle") return
    let cancelled = false

    async function poll() {
      let status: TelegramLoginStatus
      try {
        status = await api.telegramLoginStatus(agentId, loginId!)
      } catch {
        return // transient; the next tick retries
      }
      if (cancelled) return
      if (status.status === "waiting_qr") {
        if (status.qr_image) setQrImage(status.qr_image)
        setPhase("qr")
      } else if (status.status === "password_needed") {
        setPasswordError(status.password_error)
        setPhase("password")
      } else if (status.status === "authorized") {
        setConnection(status.connection)
        resetLogin()
        toast.success(t("telegramPanel.toasts.connected"))
      } else if (status.status === "error") {
        toast.error(status.error ?? t("telegramPanel.toasts.loginFailed"))
        resetLogin()
      }
    }

    const timer = setInterval(() => void poll(), POLL_INTERVAL_MS)
    void poll()
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [agentId, loginId, phase, resetLogin, t])

  async function startLogin() {
    setBusy(true)
    try {
      const start = await api.startTelegramLogin(agentId)
      setLoginId(start.login_id)
      setQrImage(start.qr_image)
      setPhase("qr")
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("telegramPanel.toasts.startFailed"))
    } finally {
      setBusy(false)
    }
  }

  async function cancelLogin() {
    const id = loginId
    resetLogin()
    if (id) await api.cancelTelegramLogin(agentId, id).catch(() => undefined)
  }

  async function submitPassword() {
    if (!loginId || !password) return
    setBusy(true)
    try {
      await api.submitTelegramPassword(agentId, loginId, password)
      setPassword("")
      setPasswordError(null)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("telegramPanel.toasts.loginFailed"))
    } finally {
      setBusy(false)
    }
  }

  async function retest() {
    setBusy(true)
    try {
      const row = await api.testTelegramConnection(agentId)
      setConnection(row)
      if (row.status === "ok") toast.success(t("telegramPanel.toasts.testOk"))
      else toast.error(row.last_error ?? t("telegramPanel.toasts.testFailed"))
    } catch {
      toast.error(t("telegramPanel.toasts.testFailed"))
    } finally {
      setBusy(false)
    }
  }

  async function disconnect() {
    if (!window.confirm(t("telegramPanel.disconnectConfirm"))) return
    setBusy(true)
    try {
      await api.deleteTelegramConnection(agentId)
      setConnection(null)
    } catch {
      toast.error(t("telegramPanel.toasts.disconnectFailed"))
    } finally {
      setBusy(false)
    }
  }

  const accountName = connection
    ? (connection.username ? `@${connection.username}` : null) ??
      [connection.first_name, connection.last_name].filter(Boolean).join(" ") ??
      connection.phone ??
      t("telegramPanel.unknownAccount")
    : null

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("telegramPanel.title")}</CardTitle>
        <CardDescription>{t("telegramPanel.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" />
            {t("telegramPanel.loading")}
          </p>
        ) : !configured ? (
          <Alert>
            <AlertTitle>{t("telegramPanel.unavailableTitle")}</AlertTitle>
            <AlertDescription>{t("telegramPanel.unavailableHint")}</AlertDescription>
          </Alert>
        ) : (
          <>
            {connection && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
                <div>
                  <p className="text-sm font-medium">{accountName}</p>
                  {connection.phone && (
                    <p className="text-muted-foreground text-xs">{connection.phone}</p>
                  )}
                  {connection.status === "error" && connection.last_error && (
                    <p className="text-destructive mt-1 text-xs">{connection.last_error}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {connection.status === "error" ? (
                    <Badge variant="destructive">{t("telegramPanel.status.error")}</Badge>
                  ) : (
                    <Badge variant="secondary">{t("telegramPanel.status.ok")}</Badge>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={busy}
                    onClick={() => void retest()}
                    aria-label={t("telegramPanel.testAria")}
                  >
                    <RefreshCw className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={busy}
                    onClick={() => void disconnect()}
                    aria-label={t("telegramPanel.disconnectAria")}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
            )}

            {phase === "idle" ? (
              <Button
                type="button"
                variant={connection ? "outline" : "default"}
                size="sm"
                disabled={busy}
                onClick={() => void startLogin()}
              >
                {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                {connection
                  ? t("telegramPanel.reconnectButton")
                  : t("telegramPanel.connectButton")}
              </Button>
            ) : phase === "qr" ? (
              <div className="space-y-3">
                <ol className="text-muted-foreground list-decimal space-y-1 pl-5 text-sm">
                  <li>{t("telegramPanel.qrStep1")}</li>
                  <li>{t("telegramPanel.qrStep2")}</li>
                  <li>{t("telegramPanel.qrStep3")}</li>
                </ol>
                {qrImage ? (
                  <img
                    src={qrImage}
                    alt={t("telegramPanel.qrAlt")}
                    className="size-48 rounded-lg border bg-white p-2"
                  />
                ) : (
                  <Loader2 className="size-6 animate-spin" />
                )}
                <Button type="button" variant="ghost" size="sm" onClick={() => void cancelLogin()}>
                  {t("telegramPanel.cancelButton")}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="tg-password">{t("telegramPanel.passwordLabel")}</Label>
                <p className="text-muted-foreground text-xs">{t("telegramPanel.passwordHint")}</p>
                <div className="flex gap-2">
                  <Input
                    id="tg-password"
                    type="password"
                    autoComplete="off"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") void submitPassword()
                    }}
                  />
                  <Button type="button" disabled={busy || !password} onClick={() => void submitPassword()}>
                    {busy && <Loader2 className="size-4 animate-spin" />}
                    {t("telegramPanel.submitPasswordButton")}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => void cancelLogin()}>
                    {t("telegramPanel.cancelButton")}
                  </Button>
                </div>
                {passwordError && <p className="text-destructive text-xs">{passwordError}</p>}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
